import { GetServerSidePropsContext, GetStaticPropsContext } from 'next';
import { PROD_DOMAIN_CORE } from 'src/api/constants';
import {
  isAuthorizationCookieExistsOnServerSide,
  safeJSONParse,
  selectedLocationCookieName,
  zustandAuthorizationStorageName
} from 'src/utils/cookieHelper';
import requestIp from 'request-ip';
import getMultyLevelCategories from 'src/api/requests/getMultyLevelCategories';
import { FirstCategoryLevel, UserLocation } from 'src/api/types';
import { ParsedUrlQuery } from 'querystring';
import logger from 'src/utils/logger';
import { processIfModifiedSinceHeader } from './ifModifiedSinceHandler';

export interface GeneralServerProps {
  ip: string | null;
  hostURL: string;
  authorizationTokenExists: boolean;
  multyLevelCategoriesList?: FirstCategoryLevel[];
  serverUserLocation: UserLocation;
}

export const retriveSSRUserIP = (
  context: GetServerSidePropsContext
): string | string[] => {
  const detectedIp = requestIp.getClientIp(context?.req);
  return detectedIp || context?.req?.socket?.remoteAddress;
};

export const generalSSRProps = async (
  context: GetServerSidePropsContext
): Promise<GeneralServerProps> => {
  processIfModifiedSinceHeader(context?.req, context?.res);
  const multyLevelCategoriesList = await getMultyLevelCategories({
    locale: context?.locale
  });
  const serverUserLocation = safeJSONParse(
    context.req.cookies[selectedLocationCookieName]
  );

  return {
    multyLevelCategoriesList,
    ip: (retriveSSRUserIP(context) as string) || null,
    hostURL: context?.req?.headers?.host,
    // This property doesn't define if the user is actually authorized. It verifies for cookie only
    authorizationTokenExists: isAuthorizationCookieExistsOnServerSide(context),
    serverUserLocation: serverUserLocation ?? null
  };
};

export const generalSPProps = async (
  context: GetStaticPropsContext
): Promise<Partial<GeneralServerProps>> => {
  const multyLevelCategoriesList = await getMultyLevelCategories({
    locale: context?.locale
  });

  return {
    hostURL: PROD_DOMAIN_CORE,
    multyLevelCategoriesList: multyLevelCategoriesList ?? null
  };
};

export const setSSRCacheControl = (
  context: GetServerSidePropsContext
): void => {
  context?.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  );
};

export const getCacheConfig = (context: GetServerSidePropsContext) => {
  const serverAuthZustandStore = safeJSONParse(
    context.req.cookies[zustandAuthorizationStorageName]
  );

  return {
    url: context.resolvedUrl,
    method: context.req.method,
    isPage: true,
    data: {
      locale: context.locale,
      location: context.req.cookies[selectedLocationCookieName],
      authorizationToken: serverAuthZustandStore?.state?.userToken
    }
  };
};

// This methods returns false if query includes not-allowed query params.
// Example: /tradename/tsytykolin/helpers/helpers/uri contains "helpers" and "uri" which are not allowed, method returns false
// Example: /tradename/tsytykolin/ is a valid url, methods returns true
export const isQueryParamsValid = (query: ParsedUrlQuery) => {
  try {
    const queryList = [
      ...Object.keys(query),
      ...((query?.nextParams as string[]) ?? [])
    ];
    const invalidNestedParams = [
      'helpers',
      'data',
      'migrations',
      'init',
      'terms',
      'params',
      'utils',
      'uri',
      'cookies'
    ];
    return !invalidNestedParams.some((key) => queryList.includes(key));
  } catch (error) {
    logger.log('isQueryParamsValid failed', error);
  }
};
